import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    IconButton,
    CircularProgress,
    InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import InputField from '../InputField/InputField';
import { ActionButton, CancelButton } from '../Button';
import { userService, ChangePasswordRequest } from '../../services/userService';
import { useResponsiveToast } from '../Toast/Toast';
import { passwordExpiryUtils } from '../../utils/passwordExpiryUtils';

interface ChangePasswordModalProps {
    open: boolean;
    onClose: () => void;
    persistent?: boolean; // If true, modal cannot be closed by clicking outside or escape key
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ open, onClose, persistent = false }) => {
    const { success: showSuccess, error: showError } = useResponsiveToast();
    
    const [formData, setFormData] = useState<ChangePasswordRequest>({
        old_password: '',
        new_password: '',
        confirm_new_password: '',
    });
    
    const [loading, setLoading] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const handleInputChange = (field: keyof ChangePasswordRequest, value: string) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const validateForm = (): boolean => {
        if (!formData.old_password.trim()) {
            showError('Current password is required');
            return false;
        }
        if (!formData.new_password.trim()) {
            showError('New password is required');
            return false;
        }
        if (formData.new_password !== formData.confirm_new_password) {
            showError('New passwords do not match');
            return false;
        }
        if (formData.old_password === formData.new_password) {
            showError('New password must be different from current password');
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setLoading(true);
            await userService.changePassword(formData);
            showSuccess('Password changed successfully!');
            
            // Clear password expiry data since password was changed
            passwordExpiryUtils.clearPasswordExpiry();
            
            // Reset form and close modal
            setFormData({
                old_password: '',
                new_password: '',
                confirm_new_password: '',
            });
            setShowOldPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
            onClose();
        } catch (err) {
            showError(err instanceof Error ? err.message : 'Failed to change password');
            console.error('Error changing password:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading && !persistent) {
            setFormData({
                old_password: '',
                new_password: '',
                confirm_new_password: '',
            });
            setShowOldPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={persistent ? () => {} : handleClose}
            maxWidth="sm"
            fullWidth
            disableEscapeKeyDown={persistent}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    p: 0,
                }
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 20,
                    fontWeight: 600,
                    color: '#132F5F',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pb: 1,
                }}
            >
                Change Password
                {!persistent && (
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            color: '#666',
                            '&:hover': {
                                backgroundColor: '#F5F5F5',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 2 }}>
                <Stack spacing={3}>
                    <InputField
                        label='Current Password'
                        type={showOldPassword ? 'text' : 'password'}
                        placeholder='Enter current password'
                        required
                        value={formData.old_password}
                        onChange={(e) => handleInputChange('old_password', e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowOldPassword(!showOldPassword)}
                                        edge="end"
                                        disabled={loading}
                                        sx={{ color: '#666' }}
                                    >
                                        {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: '100%',
                            height: '56px',
                        }}
                    />

                    <InputField
                        label='New Password'
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder='Enter new password'
                        required
                        value={formData.new_password}
                        onChange={(e) => handleInputChange('new_password', e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        edge="end"
                                        disabled={loading}
                                        sx={{ color: '#666' }}
                                    >
                                        {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: '100%',
                            height: '56px',
                        }}
                    />

                    <InputField
                        label='Confirm New Password'
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder='Confirm new password'
                        required
                        value={formData.confirm_new_password}
                        onChange={(e) => handleInputChange('confirm_new_password', e.target.value)}
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        edge="end"
                                        disabled={loading}
                                        sx={{ color: '#666' }}
                                    >
                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{
                            width: '100%',
                            height: '56px',
                        }}
                    />

                </Stack>
            </DialogContent>

            <DialogActions sx={{ 
                px: 3, 
                py: 2, 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                justifyContent: { xs: 'stretch', sm: 'center' },
                '& > :not(style) ~ :not(style)': {
                    marginLeft: { xs: 0, sm: '8px' },
                    marginTop: { xs: '8px', sm: 0 },
                }
            }}>
                {!persistent && (
                    <CancelButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{
                            borderColor: '#E53935',
                            color: '#E53935',
                            borderRadius: '12px',
                            px: 3,
                            py: 1.5,
                            fontFamily: 'Poppins, sans-serif',
                            fontWeight: 500,
                            fontSize: 14,
                            width: { xs: '100%', sm: '180px' },
                            order: { xs: 2, sm: 1 },
                            '&:hover': {
                                borderColor: '#E53935',
                                backgroundColor: 'transparent',
                            },
                            '&:disabled': {
                                borderColor: '#ccc',
                                color: '#ccc',
                            },
                        }}
                    >
                        Cancel
                    </CancelButton>
                )}
                
                <ActionButton
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                        backgroundColor: '#001F5B',
                        px: 4,
                        py: 1.5,
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: 500,
                        fontSize: 14,
                        width: persistent ? { xs: '100%', sm: '200px' } : { xs: '100%', sm: '180px' },
                        order: { xs: 1, sm: 2 },
                        '&:hover': {
                            backgroundColor: '#001742',
                        },
                        '&:disabled': {
                            backgroundColor: '#ccc',
                        },
                    }}
                >
                    {loading ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        'Change Password'
                    )}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordModal;
